import { Fragment, useRef, useState } from 'react';

import CleanButton from '../../../components/CleanButton/CleanButton';
import HorizontalLine from '../../../components/HorizontalLine/HorizontalLine';
import IconInCircle from '../../../components/IconInCircle/IconInCircle';
import Spacer from '../../../components/Spacer/Spacer';
import Stack from '../../../components/Stack/Stack';
import Text from '../../../components/Text/Text';
import TopRightPopOver from '../../../components/TopRightPopOver/TopRightPopOver';
import useOnClickOutside from '../../../hooks/input/useOnClickOutside';
import useTrans from '../../../hooks/ui/useTrans';
import useDraftBookingStepProps from '../../pages/BookingForm/hooks/useDraftBookingStepProps';
import LoggedInCustomer from '../../types/LoggedInCustomer';
import NavigationItem from '../../types/NavigationItem';
import LogOutButton from '../LogOutButton/LogOutButton';

import NavigationOption from './components/NavigationOption';
import closeIcon from './graphics/close.svg?raw';
import hamburgerIcon from './graphics/hamburger.svg?raw';

import styles from './Navigation.module.css';
import buttonStyles from './components/NavigationOption.module.css';

type NavigationProps = {
    items: Array<NavigationItem>;
    loggedInCustomer?: LoggedInCustomer;
    preventLoginOrLogout?: boolean;
};

const Navigation = ({
    items,
    loggedInCustomer,
    preventLoginOrLogout,
}: NavigationProps) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const trans = useTrans('CustomerArea.Shared.Navigation.');

    const filteredNavigationItems = items.filter((item) => {
        return !item.hideWhenInlineLoginEnabled || !preventLoginOrLogout;
    });

    /**
     * The booking is only available in later steps of the booking form.
     */
    const { booking } = useDraftBookingStepProps();

    const handleClickOutside = () => {
        setTimeout(() => setIsOpen(false), 10);
    };
    useOnClickOutside(rootRef, handleClickOutside);

    return (
        <div ref={rootRef}>
            <CleanButton onClick={() => setIsOpen(!isOpen)}>
                <IconInCircle
                    src={isOpen ? closeIcon : hamburgerIcon}
                    size="xxlarge"
                />
            </CleanButton>

            <div className={styles.PopoutWrapper}>
                <TopRightPopOver isOpen={isOpen} width={300} noPadding>
                    <Stack space="none">
                        <Spacer vertical="medium" />

                        {filteredNavigationItems.map((item, index) => (
                            <Fragment key={item.labelKey}>
                                <NavigationOption item={item} />

                                {(index < filteredNavigationItems.length - 1 ||
                                    loggedInCustomer) && (
                                    <HorizontalLine subtle />
                                )}
                            </Fragment>
                        ))}

                        {loggedInCustomer && !preventLoginOrLogout && (
                            <LogOutButton
                                actionUrl={loggedInCustomer.logOutUrl}
                                bookingReference={booking?.reference}
                                className={buttonStyles.Root}
                                variant="white"
                                size="xsmall"
                                align="center"
                                fullWidth
                                noRoundCorners
                            >
                                <Text
                                    lineHeight="small"
                                    size="large"
                                    bold
                                    noWrapping
                                >
                                    {trans('LogOut')}
                                </Text>
                            </LogOutButton>
                        )}

                        <Spacer vertical="medium" />
                    </Stack>
                </TopRightPopOver>
            </div>
        </div>
    );
};

export default Navigation;
